/**
 * Helpers de normalización del `value` de una sección rich_text.
 *
 * Convive con dos formatos:
 *
 *   1. **TipTap JSON** (objeto `{ type: 'doc', content: [...] }`). Es
 *      lo que el editor produce y persiste el backend desde B.6.b en
 *      adelante. Diff estructural amigable a Change Impact (Doc 08) y
 *      a chunking semántico cuando entre RAG (Sprint C de Sources).
 *
 *   2. **String plano legacy** (textarea simple, doble salto = párrafo
 *      nuevo). Lo que producía el SectionEditor antiguo en proyectos
 *      creados antes del editor TipTap.
 *
 * `coerceToTipTapDoc` recibe cualquier `value` desconocido y devuelve
 * un doc TipTap válido, listo para `editor.setContent()`. Idempotente:
 * si ya es un doc, lo retorna tal cual.
 *
 * `markdownToTipTapDoc` (Sprint AI-1.d.b): conversión markdown → HTML
 * → JSON ProseMirror. Lo usa `SectionEditor.handleAiAccept` cuando el
 * agente IA devuelve markdown para una sección rich_text — sin esto
 * el editor mostraría `**texto**` literal.
 */

import { generateJSON } from '@tiptap/html'
import type { JSONContent } from '@tiptap/react'
import { marked } from 'marked'

import { CITATION_INLINE_RE } from './CitationMark'
import { buildEditorExtensions } from './extensions'

export type RichTextValue = JSONContent | string | null | undefined

/** Doc vacío canónico (un solo párrafo vacío). */
export function emptyDoc(): JSONContent {
  return { type: 'doc', content: [{ type: 'paragraph' }] }
}

/** True si el value parece ser un doc TipTap ya (`{ type: 'doc', ... }`). */
export function isTipTapDoc(value: unknown): value is JSONContent {
  return (
    typeof value === 'object' &&
    value !== null &&
    !Array.isArray(value) &&
    'type' in value &&
    (value as { type: unknown }).type === 'doc'
  )
}

/** Normaliza cualquier value a doc TipTap.
 *
 *  - JSONContent doc → tal cual.
 *  - string → parsea como markdown via `markdownToTipTapDoc` (Sprint
 *    AI-1.g 5.4-fix). Necesario porque el orchestrator Generate-all
 *    persiste el output del SectionDraftAgent como markdown raw — sin
 *    el parseo el editor mostraría `## titulo` y `**bold**` literales.
 *    Backwards-compatible con strings legacy sin formato: marked los
 *    convierte en párrafos planos equivalentes a `stringToDoc`.
 *  - null / undefined / "" → doc vacío.
 *  - Cualquier otra cosa (number, array, doc malformado) → doc vacío
 *    (defensivo — el editor crashea si recibe un doc no-válido).
 */
export function coerceToTipTapDoc(value: unknown): JSONContent {
  if (isTipTapDoc(value)) {
    // Aún así validamos que tenga `content` (TipTap exige al menos un
    // párrafo). Si está vacío, le damos uno.
    const doc = value as JSONContent
    if (!doc.content || doc.content.length === 0) {
      return emptyDoc()
    }
    return doc
  }
  if (typeof value === 'string') {
    return markdownToTipTapDoc(value)
  }
  return emptyDoc()
}

/** Convierte string plano legacy a doc TipTap. Doble salto = párrafo
 * nuevo. Salto simple = hard break dentro del párrafo. */
export function stringToDoc(text: string): JSONContent {
  const trimmed = text.trim()
  if (!trimmed) return emptyDoc()

  // Splitear por blank-lines (uno o más \n consecutivos) para detectar
  // párrafos. Cada párrafo a su vez puede tener saltos simples → hard
  // breaks.
  const blocks = trimmed.split(/\n\s*\n+/)
  const paragraphs: JSONContent[] = blocks.map((block) => {
    const lines = block.split('\n')
    const inline: JSONContent[] = []
    lines.forEach((line, idx) => {
      if (line.length > 0) {
        inline.push({ type: 'text', text: line })
      }
      if (idx < lines.length - 1) {
        inline.push({ type: 'hardBreak' })
      }
    })
    return inline.length > 0
      ? { type: 'paragraph', content: inline }
      : { type: 'paragraph' }
  })
  return { type: 'doc', content: paragraphs }
}

/** Convierte markdown a doc TipTap usando marked + generateJSON.
 *
 * Pipeline:
 *   1. `marked.parse(md)` → HTML (lib estándar, sync mode).
 *   2. `generateJSON(html, extensions)` → ProseMirror JSON Doc usando
 *      las MISMAS extensiones del editor real (StarterKit + Highlight +
 *      Link + Underline + sub/super + tables + etc.). Esto garantiza
 *      paridad: el draft generado se ve igual que cuando un humano
 *      escribe en el editor.
 *
 * Si la conversión falla (markdown inválido, edge case en marked, etc.),
 * caemos a `stringToDoc` que al menos preserva los párrafos sin
 * interpretar el formato. El user verá texto plano, pero no un crash.
 */
export function markdownToTipTapDoc(markdown: string): JSONContent {
  const trimmed = markdown.trim()
  if (!trimmed) return emptyDoc()
  try {
    // marked puede correr async desde v9+; forzamos sync para que
    // handleAiAccept no tenga que ser async (cambiaría su firma).
    const html = marked.parse(trimmed, { async: false }) as string
    const extensions = buildEditorExtensions()
    const doc = generateJSON(html, extensions) as JSONContent
    // Validación defensiva — si generateJSON devolvió algo raro, fallback.
    if (!doc || doc.type !== 'doc' || !Array.isArray(doc.content)) {
      return stringToDoc(markdown)
    }
    // ADR-0001 Tier 1 — wrap `[N]` con CitationMark. Lo hacemos POST
    // generateJSON para no depender de que marked/HTML preserve los
    // corchetes (los preserva, pero el wrap se hace contra el JSON
    // ya parseado, que es más estable que parsear HTML otra vez).
    return wrapInlineCitations(doc)
  } catch {
    // Fallback robusto — el user puede editar manualmente.
    return stringToDoc(markdown)
  }
}

/** Recorre el doc TipTap y reemplaza ocurrencias de `[N]` dentro de
 * text nodes por text nodes con `marks: [{type: 'citation', attrs:
 * {refNumber: N}}]`. Preserva otras marks (bold, italic, link).
 *
 * No modifica el doc original — devuelve uno nuevo. ADR-0001 Tier 1.
 */
export function wrapInlineCitations(doc: JSONContent): JSONContent {
  const next: JSONContent = {
    ...doc,
    content: doc.content?.map(walkNode).filter(Boolean) as JSONContent[],
  }
  return next
}

function walkNode(node: JSONContent): JSONContent {
  if (node.type === 'text' && typeof node.text === 'string') {
    return splitTextNodeForCitations(node)
  }
  if (Array.isArray(node.content)) {
    return {
      ...node,
      content: node.content.flatMap(flattenWalkResult),
    }
  }
  return node
}

/** Como `walkNode` pero el resultado puede ser uno o varios nodos
 * inline cuando un text node se parte por matches `[N]`. */
function flattenWalkResult(node: JSONContent): JSONContent[] {
  if (node.type === 'text' && typeof node.text === 'string') {
    return splitTextNode(node)
  }
  if (Array.isArray(node.content)) {
    return [
      {
        ...node,
        content: node.content.flatMap(flattenWalkResult),
      },
    ]
  }
  return [node]
}

/** Para uso en el primer nivel del doc — devuelve UN nodo (los
 * párrafos no se parten, solo sus inline children). */
function splitTextNodeForCitations(node: JSONContent): JSONContent {
  // Cuando llegamos aquí significa que el doc tiene un text node de
  // primer nivel (raro pero defensivo). Lo dejamos sin tocar — la
  // sustitución real ocurre en `splitTextNode` al recorrer hijos.
  return node
}

/** Parte un text node en múltiples cuando contiene `[N]`. Los segments
 * de marker reciben la mark `citation`; los segments restantes
 * preservan las marks originales. */
function splitTextNode(node: JSONContent): JSONContent[] {
  const text = node.text ?? ''
  if (!text) return [node]
  const re = new RegExp(CITATION_INLINE_RE.source, 'g')
  const segments: JSONContent[] = []
  let cursor = 0
  let match: RegExpExecArray | null
  const baseMarks = Array.isArray(node.marks) ? node.marks : []
  while ((match = re.exec(text)) !== null) {
    const start = match.index
    const end = start + match[0].length
    if (start > cursor) {
      segments.push({
        type: 'text',
        text: text.slice(cursor, start),
        ...(baseMarks.length > 0 ? { marks: [...baseMarks] } : {}),
      })
    }
    const refNumber = Number.parseInt(match[1] ?? '0', 10)
    segments.push({
      type: 'text',
      text: text.slice(start, end),
      marks: [
        ...baseMarks.filter((m) => m.type !== 'citation'),
        { type: 'citation', attrs: { refNumber } },
      ],
    })
    cursor = end
  }
  if (cursor === 0) {
    return [node]
  }
  if (cursor < text.length) {
    segments.push({
      type: 'text',
      text: text.slice(cursor),
      ...(baseMarks.length > 0 ? { marks: [...baseMarks] } : {}),
    })
  }
  return segments
}

/** True si el doc no tiene contenido visible (solo párrafos vacíos
 * y/o whitespace). Útil para decidir mostrar empty state en el viewer. */
export function isDocEmpty(value: unknown): boolean {
  if (!isTipTapDoc(value)) {
    return value === null || value === undefined || value === ''
  }
  const doc = value as JSONContent
  if (!doc.content || doc.content.length === 0) return true
  return doc.content.every((node) => {
    if (!node.content || node.content.length === 0) return true
    return node.content.every((child) => {
      if (child.type === 'text') {
        return !child.text || child.text.trim() === ''
      }
      return false
    })
  })
}
